import {
  LandingSignupFlowActionType,
  type LandingPageConfig,
  type LandingSectionType,
} from "@fitness-app/data-models/entities/LandingPage";
import { funnelSectionDefinitions } from "@fitness-app/data-models/landing-templates/index";

export const landingSectionTypeOptions: { label: string; value: LandingSectionType }[] = [
  { label: "Header V2", value: "headerV2" },
  { label: "Header classic", value: "headerClassic" },
  { label: "Hollywood hero", value: "hollywoodHero" },
  { label: "Metamorfoza hero/video", value: "metamorfozaHeroVideo" },
  { label: "Promo video", value: "promoVideo" },
  { label: "Form section", value: "formSection" },
  { label: "Hero rating", value: "heroRating" },
  { label: "Transformation carousel", value: "transformationCarousel" },
  { label: "Statistics", value: "statisticsSection" },
  { label: "Did you know", value: "didYouKnow" },
  { label: "Features carousel", value: "featuresCarousel" },
  { label: "Quiz banner", value: "quizBanner" },
  { label: "Steps timeline", value: "stepsTimeline" },
  { label: "Practice carousel", value: "practiceCarousel" },
  { label: "Metamorfozy CTA", value: "metamorfozyCta" },
  { label: "Hollywood FAQ", value: "hollywoodFaq" },
  { label: "Rating", value: "ratingSection" },
  { label: "Hollywood footer", value: "hollywoodFooter" },
  { label: "Pakiety hero", value: "pakietyHero" },
  { label: "Pakiety cards", value: "pakietyCards" },
  { label: "Pakiety static section", value: "pakietyStaticSection" },
  { label: "Pakiety statistics", value: "pakietyStatistics" },
  { label: "Sticky pakiety CTA", value: "stickyPakietyCta" },
  { label: "Reviews", value: "reviews" },
  { label: "Why us", value: "whyUs" },
  { label: "Basic FAQ", value: "basicFaq" },
  { label: "Transformations grid", value: "transformationsGrid" },
  { label: "Campaign banner", value: "campaignBanner" },
  { label: "Campaign countdown (mobile)", value: "campaignCountdown" },
  { label: "Transformations classic", value: "transformationsClassic" },
  { label: "Reviews classic", value: "reviewsClassic" },
  { label: "Footer classic", value: "footerClassic" },
  // Sections of the funnel templates (TRE-2356).
  ...Object.entries(funnelSectionDefinitions).map(([value, definition]) => ({
    label: `${definition!.group}: ${definition!.label}`,
    value: value as LandingSectionType,
  })),
];

export const pakietyStaticSectionOptions = [
  { label: "Wystarczy", value: "wystarczy" },
  { label: "Nie musisz", value: "nieMusisz" },
  { label: "Musisz", value: "musisz" },
  { label: "Program Hollywood", value: "programHollywood" },
  { label: "Testimonial", value: "testimonial" },
  { label: "Co się zmieni", value: "whatChange" },
  { label: "Forgot about it", value: "forgotAboutIt" },
  { label: "Last metamorphose", value: "lastMetamorphose" },
  { label: "Your choice", value: "yourChoice" },
  { label: "Our evidences", value: "ourEvidences" },
  { label: "Different season", value: "differentSeason" },
  { label: "Benefits", value: "benefits" },
  { label: "We help", value: "weHelp" },
  { label: "Comparison", value: "comparison" },
  { label: "Ideal solution", value: "idealSolution" },
  { label: "Pakiety FAQ", value: "faq" },
  { label: "Guarantee", value: "guarantee" },
  { label: "Intro story", value: "introStory" },
  { label: "Steps", value: "steps" },
];

export const getDefaultSectionProps = (type: LandingSectionType): Record<string, unknown> => {
  switch (type) {
    case "headerV2":
      return {
        ctaLabel: "Dopasuj program",
        showMobileCta: true,
        ctaClassName: "bg-[#ff4151] text-white",
      };
    case "headerClassic":
      return {
        ctaLabel: "Rozpocznij metamorfozę",
        ctaClassName: "bg-[#ff4151] text-white hover:opacity-90",
      };
    case "hollywoodHero":
      return {
        badge: "🚀 JUŻ JEST!",
        headline: "Program Hollywood skierowany dla kobiet 35+, które chcą dokonać ostatecznej metamorfozy sylwetki",
        cta: "Sprawdź dopasowanie programu",
        guarantee: "6 miesięczna gwarancja uzyskania efektów",
        desktopVideoSrc: "/assets/hollywood/Hollywood_Desktop.mp4",
        mobileVideoSrc: "/assets/hollywood/Hollywood_Vertical.mp4",
        desktopPosterSrc: "/assets/hollywood/Hollywood_Desktop.jpg",
        mobilePosterSrc: "/assets/hollywood/Hollywood_Vertical.jpg",
      };
    case "metamorfozaHeroVideo":
      return {
        description: "Dlaczego większość kobiet potrafi schudnąć - ale nie utrzymać efektów po 35 roku życia?",
        content:
          "Jeżeli masz wrażenie, że próbowałaś już wszystkiego - to nagranie pokaże Ci, dlaczego efekty nigdy nie zostawały na stałe.",
      };
    case "promoVideo":
      return {
        title: "Obejrzyj wideo teraz:",
        description: "",
        videoId: "",
        id: "promo-video",
        showCirlces: true,
        hideIcons: true,
      };
    case "formSection":
      return {
        ctaText: "Rozpocznij metamorfozę",
        heading: "Czy chcesz schudnąć na zawsze?",
        description: "Program Hollywood pomógł ponad 18,000 kobiet dokonać ostatecznej metamorfozy sylwetki.",
        benefitsTitle: "Umów się na bezpłatną konsultację i dowiedz się:",
        benefits: [
          "Co blokuje Twoją metamorfozę",
          "Dlaczego wcześniejsze diety nie działały",
          "Jaki plan da Ci trwałe efekty",
        ],
      };
    case "quizBanner":
      return {
        heading: "Chcesz rozpocząć swoją ostateczną metamorfozę?",
        description: "",
        cta: "Sprawdź dopasowanie",
      };
    case "metamorfozyCta":
      return {
        heading: "Czy jesteś gotowa na ostateczną metamorfozę i osiągnąć swój cel?",
        cta: "Sprawdź dopasowanie",
      };
    case "hollywoodFaq":
    case "basicFaq":
      return {
        heading: "Najczęściej zadawane pytania",
        items: [
          {
            emoji: "✨",
            question: "Czy program jest dla mnie?",
            answer: "Tak, jeśli chcesz przejść proces z opieką i konkretnym planem działania.",
          },
        ],
      };
    case "hollywoodFooter":
      return {
        variant: "default",
        heading: "Zacznij swoją metamorfozę",
        description: "Sprawdź dopasowanie programu i zobacz, który wariant będzie dla Ciebie najlepszy.",
        cta: "Sprawdź dopasowanie",
        metamorphosesCount: "Ponad 18 000 metamorfoz",
        copyright: "© Program Hollywood",
      };
    case "pakietyStaticSection":
      return { variant: "wystarczy" };
    case "pakietyCards":
      return { showGradient: false };
    case "pakietyStatistics":
      return { kind: "pakiety" };
    case "stickyPakietyCta":
      return {
        startSelector: "[data-sticky-cta-start]",
        endSelector: "#packages",
      };
    case "reviews":
    case "transformationsGrid":
    case "transformationsClassic":
      return { className: "" };
    case "reviewsClassic":
      return { className: "", ctaLabel: "" };
    case "footerClassic":
      return { className: "", showThemeSwitcher: false };
    default:
      return { ...funnelSectionDefinitions[type]?.defaultProps };
  }
};

export const createDefaultLandingConfig = (slug: string): LandingPageConfig => ({
  seo: {
    title: "Program Hollywood",
    description: "Landing Programu Hollywood.",
    ogImage: "/assets/main.jpeg",
    facebookDomainVerification: "jdtjx4q9c5kqlid5h5qb3ayhnixrr1",
    favicon: "/favicon.png",
  },
  tracking: {
    pageName: slug || "hollywood",
    facebookPixelId: "1325719371335707",
    googleAnalyticsId: "G-GTXYBSFFKC",
  },
  layout: {
    forcedTheme: "light",
    visualTheme: "pink",
    fontPreset: "hollywood",
    backgroundClassName: "bg-[#f3f3f3]",
  },
  signupFlow: {
    eventTypeUri: "https://calendly.com/sylwetkahollywood/metamorfoza-hollywood-2-2026",
    questions: [],
    colorTheme: "pink",
    storageKeyPrefix: slug.replace(/\W+/g, "_") || "hollywood",
    disableCache: true,
    qualifiedAction: { type: LandingSignupFlowActionType.GoToPackagesPage },
    packageSelectAction: { type: LandingSignupFlowActionType.GoToCheckoutVariant },
    bookingCompleteAction: { type: LandingSignupFlowActionType.GoToBeforeConsultation },
  },
  sections: [
    {
      id: "header",
      type: "headerV2",
      enabled: true,
      trackingName: "header",
      props: getDefaultSectionProps("headerV2"),
    },
    {
      id: "hero",
      type: "hollywoodHero",
      enabled: true,
      trackingName: "hero",
      props: getDefaultSectionProps("hollywoodHero"),
    },
    {
      id: "footer",
      type: "hollywoodFooter",
      enabled: true,
      trackingName: "footer",
      props: getDefaultSectionProps("hollywoodFooter"),
    },
  ],
});
